import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  Database, Download, Upload, Trash2, Loader2, Check, CloudOff, Cloud, RefreshCw, AlertTriangle, HardDrive, Clock, Shield
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBackup() {
  const [searchParams] = useSearchParams();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [driveStatus, setDriveStatus] = useState({ connected: false });
  const [driveLoading, setDriveLoading] = useState(true);
  const [settings, setSettings] = useState({ auto_backup_enabled: false, backup_time: "02:00", retention_days: 7 });
  const [savingSettings, setSavingSettings] = useState(false);
  const [restoreDialog, setRestoreDialog] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [uploadRestoring, setUploadRestoring] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [backupLogs, setBackupLogs] = useState([]);
  const [purgeDialog, setPurgeDialog] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [purging, setPurging] = useState(false);

  useEffect(() => {
    fetchBackups();
    fetchDriveStatus();
    fetchSettings();
    fetchBackupLogs();
    if (searchParams.get("drive_connected") === "true") {
      toast.success("Google Drive connected successfully!");
    }
    if (searchParams.get("drive_error")) {
      toast.error(`Drive connection failed: ${searchParams.get("drive_error")}`);
    }
  }, []);

  const fetchBackupLogs = async () => {
    try {
      const res = await fetch(`${API}/backup/logs`, { credentials: "include" });
      if (res.ok) setBackupLogs(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchBackups = async () => {
    try {
      const res = await fetch(`${API}/backup/list`, { credentials: "include" });
      if (res.ok) setBackups(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchDriveStatus = async () => {
    try {
      const res = await fetch(`${API}/backup/drive/status`, { credentials: "include" });
      if (res.ok) setDriveStatus(await res.json());
    } catch (e) { console.error(e); }
    finally { setDriveLoading(false); }
  };

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/backup/settings`, { credentials: "include" });
      if (res.ok) setSettings(await res.json());
    } catch (e) { console.error(e); }
  };

  const connectDrive = async () => {
    try {
      const res = await fetch(`${API}/backup/drive/connect`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.authorization_url;
      }
    } catch (e) { toast.error("Failed to initiate Drive connection"); }
  };

  const disconnectDrive = async () => {
    try {
      await fetch(`${API}/backup/drive/disconnect`, { method: "POST", credentials: "include" });
      setDriveStatus({ connected: false });
      toast.success("Google Drive disconnected");
    } catch (e) { toast.error("Failed to disconnect"); }
  };

  const createBackup = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API}/backup/create`, { method: "POST", credentials: "include", signal: AbortSignal.timeout(120000) });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || `Backup failed (${res.status})`); }
      toast.success("Backup created successfully!");
      fetchBackups();
      fetchBackupLogs();
    } catch (e) { toast.error(e.message || "Backup failed"); }
    finally { setCreating(false); }
  };

  const downloadBackup = (backupId, filename) => {
    window.open(`${BACKEND_URL}/api/backup/${backupId}/download`, "_blank");
  };

  const restoreBackup = async () => {
    if (!restoreDialog) return;
    setRestoring(true);
    try {
      const res = await fetch(`${API}/backup/${restoreDialog.backup_id}/restore`, { method: "POST", credentials: "include" });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Restore failed"); }
      toast.success("Database restored successfully! Please refresh.");
      setRestoreDialog(null);
    } catch (e) { toast.error(e.message); }
    finally { setRestoring(false); }
  };

  const handleUploadRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadRestoring(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/backup/restore/upload`, { method: "POST", credentials: "include", body: formData });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Restore failed"); }
      toast.success("Database restored from uploaded file! Please refresh.");
    } catch (e) { toast.error(e.message); }
    finally { setUploadRestoring(false); e.target.value = ""; }
  };

  const deleteBackup = async () => {
    if (!deleteDialog) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/backup/${deleteDialog}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Delete failed");
      toast.success("Backup deleted");
      setDeleteDialog(null);
      fetchBackups();
    } catch (e) { toast.error(e.message); }
    finally { setDeleting(false); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API}/backup/settings`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(settings),
      });
      if (!res.ok) throw new Error("Save failed");
      toast.success("Backup settings saved!");
    } catch (e) { toast.error(e.message); }
    finally { setSavingSettings(false); }
  };

  const purgeDatabase = async () => {
    if (purgeConfirm !== "PURGE") {
      toast.error("Please type PURGE to confirm");
      return;
    }
    setPurging(true);
    try {
      const res = await fetch(`${API}/backup/purge`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ confirmation: "PURGE" }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Purge failed"); }
      toast.success("Database purged successfully! Your admin session is preserved.");
      setPurgeDialog(false);
      setPurgeConfirm("");
      fetchBackups();
    } catch (e) { toast.error(e.message); }
    finally { setPurging(false); }
  };

  const formatSize = (bytes) => {
    if (!bytes) return "N/A";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "N/A";

  return (
    <AdminLayout>
      <div className="animate-fade-in max-w-3xl" data-testid="admin-backup">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Backup & Restore</h1>
          <p className="text-sm text-gray-500 mt-1">Manage database backups with Google Drive cloud storage</p>
        </div>

        {/* Google Drive Connection */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5" data-testid="drive-connection">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${driveStatus.connected ? "bg-green-100" : "bg-gray-100"}`}>
                {driveStatus.connected ? <Cloud className="w-5 h-5 text-green-600" /> : <CloudOff className="w-5 h-5 text-gray-400" />}
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Google Drive</h2>
                <p className="text-[10px] text-gray-400">{driveStatus.connected ? "Connected - backups will upload to Sm_Backups folder" : "Not connected - backups stored locally only"}</p>
              </div>
            </div>
            {driveLoading ? (
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            ) : driveStatus.connected ? (
              <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={disconnectDrive} data-testid="disconnect-drive-btn">
                Disconnect
              </Button>
            ) : (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={connectDrive} data-testid="connect-drive-btn">
                Connect Drive
              </Button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          <button onClick={createBackup} disabled={creating}
            className="flex items-center gap-3 p-4 bg-green-50 hover:bg-green-100 border border-green-200 rounded-2xl transition-colors disabled:opacity-50" data-testid="create-backup-btn">
            {creating ? <Loader2 className="w-5 h-5 animate-spin text-green-600" /> : <Database className="w-5 h-5 text-green-600" />}
            <div className="text-left">
              <p className="font-semibold text-green-800 text-sm">{creating ? "Creating Backup..." : "Create Backup Now"}</p>
              <p className="text-xs text-green-600">Full database snapshot</p>
            </div>
          </button>
          <label className={`flex items-center gap-3 p-4 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-2xl transition-colors cursor-pointer ${uploadRestoring ? "opacity-50 pointer-events-none" : ""}`} data-testid="upload-restore-btn">
            {uploadRestoring ? <Loader2 className="w-5 h-5 animate-spin text-orange-600" /> : <Upload className="w-5 h-5 text-orange-600" />}
            <div className="text-left">
              <p className="font-semibold text-orange-800 text-sm">{uploadRestoring ? "Restoring..." : "Restore from File"}</p>
              <p className="text-xs text-orange-600">Upload a .gz backup file</p>
            </div>
            <input type="file" accept=".gz,.tar.gz" className="hidden" onChange={handleUploadRestore} disabled={uploadRestoring} />
          </label>
        </div>

        {/* Danger Zone - Purge */}
        <div className="border border-red-200 rounded-2xl p-5 mb-5" data-testid="purge-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="font-semibold text-red-700">Danger Zone</h2>
                <p className="text-[10px] text-gray-400">Permanently delete all data from the database</p>
              </div>
            </div>
            <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => { setPurgeDialog(true); setPurgeConfirm(""); }} data-testid="purge-db-btn">
              Purge Database
            </Button>
          </div>
        </div>

        {/* Auto Backup Settings */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5" data-testid="backup-settings">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Auto Backup</h2>
              <p className="text-[10px] text-gray-400">Schedule daily automatic backups</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Enable Daily Auto Backup</Label>
              <Switch checked={settings.auto_backup_enabled} onCheckedChange={(v) => setSettings(s => ({ ...s, auto_backup_enabled: v }))} data-testid="auto-backup-toggle" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Backup Time (IST, 24h)</Label>
                <Input type="time" value={settings.backup_time} onChange={(e) => setSettings(s => ({ ...s, backup_time: e.target.value }))} className="font-mono" data-testid="backup-time" />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Retention (days)</Label>
                <Input type="number" min={1} max={90} value={settings.retention_days} onChange={(e) => setSettings(s => ({ ...s, retention_days: parseInt(e.target.value) || 7 }))} data-testid="retention-days" />
              </div>
            </div>
            <Button size="sm" onClick={saveSettings} disabled={savingSettings} className="bg-purple-600 hover:bg-purple-700" data-testid="save-backup-settings-btn">
              {savingSettings ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving...</> : <><Check className="w-4 h-4 mr-1" /> Save Settings</>}
            </Button>
          </div>
        </div>

        {/* Auto-Backup Activity Log */}
        {backupLogs.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5" data-testid="backup-logs">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Scheduler Activity</h2>
                <p className="text-[10px] text-gray-400">Recent auto-backup runs</p>
              </div>
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {backupLogs.slice(0, 10).map((log, i) => (
                <div key={i} className={`flex items-center justify-between text-xs px-3 py-2 rounded-lg ${log.status === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                  <div className="flex items-center gap-2">
                    {log.status === "success" ? <Check size={12} /> : <AlertTriangle size={12} />}
                    <span className="font-medium">{log.status === "success" ? `Backup: ${log.filename}` : `Failed: ${log.error?.slice(0, 60)}`}</span>
                  </div>
                  <span className="text-[10px] opacity-70">{formatDate(log.timestamp)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Backup List */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="backup-list">
          <div className="flex items-center justify-between p-5 border-b border-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <HardDrive className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Available Backups</h2>
                <p className="text-[10px] text-gray-400">{backups.length} backup{backups.length !== 1 ? "s" : ""}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchBackups} data-testid="refresh-backups-btn">
              <RefreshCw size={14} />
            </Button>
          </div>

          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div>
          ) : backups.length === 0 ? (
            <div className="p-12 text-center">
              <Shield size={28} className="text-gray-500 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No backups yet. Create your first backup above.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {backups.map((b) => (
                <div key={b.backup_id} className="flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 transition-colors" data-testid={`backup-${b.backup_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{b.filename}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-[9px] text-gray-500">{formatDate(b.created_at)}</span>
                      <span className="text-[9px] text-gray-500">{formatSize(b.file_size)}</span>
                      {b.drive_file_id && <span className="text-[10px] text-green-600 font-medium flex items-center gap-0.5"><Cloud size={9} /> Drive</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => downloadBackup(b.backup_id, b.filename)} data-testid={`download-${b.backup_id}`}>
                      <Download size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-orange-600 hover:text-orange-700 hover:bg-orange-50" onClick={() => setRestoreDialog({ backup_id: b.backup_id, filename: b.filename })} data-testid={`restore-${b.backup_id}`}>
                      <RefreshCw size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => setDeleteDialog(b.backup_id)} data-testid={`delete-${b.backup_id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Restore Confirmation Dialog */}
        <Dialog open={!!restoreDialog} onOpenChange={() => setRestoreDialog(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-orange-600"><AlertTriangle size={18} /> Restore Database</DialogTitle>
              <DialogDescription>This will replace ALL current data with the backup. This action cannot be undone.</DialogDescription>
            </DialogHeader>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-700">
              <p className="font-medium">Restoring: {restoreDialog?.filename}</p>
            </div>
            <div className="flex gap-3 mt-2">
              <Button variant="outline" className="flex-1" onClick={() => setRestoreDialog(null)}>Cancel</Button>
              <Button className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" onClick={restoreBackup} disabled={restoring} data-testid="confirm-restore-btn">
                {restoring ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Restoring...</> : "Confirm Restore"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
          <DialogContent className="sm:max-w-xs">
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete Backup?</DialogTitle>
              <DialogDescription>This backup will be permanently removed from server and Google Drive.</DialogDescription>
            </DialogHeader>
            <div className="flex gap-3 mt-2">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteDialog(null)}>Cancel</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={deleteBackup} disabled={deleting} data-testid="confirm-delete-btn">
                {deleting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Deleting...</> : "Delete"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Purge Database Dialog */}
        <Dialog open={purgeDialog} onOpenChange={() => { setPurgeDialog(false); setPurgeConfirm(""); }}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle size={18} /> Purge Entire Database</DialogTitle>
              <DialogDescription>This will permanently delete ALL data — menu items, orders, customers, wallets, settings, everything. Your admin account and current session will be preserved.</DialogDescription>
            </DialogHeader>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 space-y-1">
              <p className="font-semibold">This action CANNOT be undone.</p>
              <p>Make sure you have a backup before proceeding.</p>
            </div>
            <div>
              <Label className="text-[10px] text-gray-400 mb-1.5 block">Type <span className="font-mono font-bold text-red-600">PURGE</span> to confirm</Label>
              <Input
                value={purgeConfirm}
                onChange={(e) => setPurgeConfirm(e.target.value)}
                placeholder="Type PURGE here"
                className="font-mono text-center tracking-widest"
                data-testid="purge-confirm-input"
              />
            </div>
            <div className="flex gap-3 mt-1">
              <Button variant="outline" className="flex-1" onClick={() => { setPurgeDialog(false); setPurgeConfirm(""); }}>Cancel</Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
                onClick={purgeDatabase}
                disabled={purging || purgeConfirm !== "PURGE"}
                data-testid="confirm-purge-btn"
              >
                {purging ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Purging...</> : "Purge Database"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
